// Branding: the platform's own face before a company is chosen (set by the platform superadmin) and each
// company's own look afterwards (set by the superadmin or the company's admin).
//
// The login screen runs before anyone is signed in, so it reads branding through two narrow database
// functions (get_platform_branding, resolve_tenant_by_code) that return only public branding fields.
//
// A company's colours are applied as CSS variables (see index.css): --brand-a / --brand-b / --brand-rgb /
// --brand-bg drive the login screen and the gold-style buttons. The app's own colour tokens (text, cards,
// borders, ring) are only overridden when a company picks colours other than the default gold-on-black, so
// Taxis Gold & Black looks exactly as it always has.
import { supabase } from "@/lib/supabase";

export interface PlatformBranding {
  product_name: string;
  product_subtitle: string | null;
  tagline: string | null;
  logo_url: string | null;
  favicon_url: string | null;
}

export interface TenantBranding {
  id: string;
  tenant_name: string;
  display_name: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  brand_primary: string | null;
  brand_secondary: string | null;
  brand_background: string | null;
  login_tagline: string | null;
}

export const DEFAULT_PLATFORM: PlatformBranding = {
  product_name: "OpsLink",
  product_subtitle: "DMS | Digital Management Suite",
  tagline: null,
  logo_url: null,
  favicon_url: null,
};

/** A company's default text/accent colours (gold). Companies choose these; they do not choose the background. */
export const DEFAULT_THEME = { primary: "#FFD700", secondary: "#FFA500" } as const;

/** The neutral OpsLink sign-in colours (chrome and purple). */
export const PLATFORM_THEME = { primary: "#E3E7EF", secondary: "#A78BFA" } as const;

/** The platform's own background: the same deep purple on every screen, for every company. */
export const PLATFORM_BACKGROUND = { top: "#2A1B4D", bottom: "#0D0919", panel: "rgba(14, 9, 28, 0.82)" } as const;

const HEX = /^#[0-9a-fA-F]{6}$/;
/** Mixes `hex` toward `toward` by `amount` (0..1). */
export function mixHex(hex: string, toward: string, amount: number): string {
  const a = hexToRgb(hex), b = hexToRgb(toward);
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * amount));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

export const isHex = (v: string | null | undefined): v is string => !!v && HEX.test(v);

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l * 100];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rr) h = ((gg - bb) / d) % 6;
  else if (max === gg) h = (bb - rr) / d + 2;
  else h = (rr - gg) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return [h, s * 100, l * 100];
}

export const hexToHslTriplet = (hex: string, lightnessShift = 0): string => {
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  const L = Math.max(0, Math.min(100, l + lightnessShift));
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(L)}%`;
};

/** Relative luminance 0..1 (WCAG). */
export function luminance(hex: string): number {
  const f = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** Black or white text, whichever reads better on this colour. */
export const readableOn = (hex: string): "#000000" | "#FFFFFF" => (luminance(hex) > 0.4 ? "#000000" : "#FFFFFF");

const sameHex = (a: string | null | undefined, b: string) => !!a && a.toLowerCase() === b.toLowerCase();

/** True when the company is on the default gold (or has set nothing), so no colour-token overrides are needed. */
export const isDefaultTheme = (t: Pick<TenantBranding, "brand_primary"> | null | undefined): boolean =>
  !t || !isHex(t.brand_primary) || sameHex(t.brand_primary, DEFAULT_THEME.primary);

/** WCAG contrast ratio between two colours (1 = identical, 21 = black on white). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** The CSS variables for a company's colours. The background is always the platform's; only text/accent colours vary. */
export function themeVariables(t: Pick<TenantBranding, "brand_primary" | "brand_secondary"> | null): Record<string, string> {
  const primary = isHex(t?.brand_primary) ? t!.brand_primary! : DEFAULT_THEME.primary;
  const secondary = isHex(t?.brand_secondary) ? t!.brand_secondary! : isHex(t?.brand_primary) ? primary : DEFAULT_THEME.secondary;

  const vars: Record<string, string> = {
    "--brand-a": primary,
    "--brand-b": secondary,
    "--brand-rgb": hexToRgb(primary).join(" "),
    "--brand-bg-1": PLATFORM_BACKGROUND.top,
    "--brand-bg-2": PLATFORM_BACKGROUND.bottom,
    "--brand-on-primary": readableOn(primary),
    "--brand-text": "#FFFFFF",
    "--brand-panel": PLATFORM_BACKGROUND.panel,
  };

  if (!isDefaultTheme(t)) {
    // The app's own text/accent tokens (shadcn-style HSL triplets), derived from the company's main colour.
    const onPrimary = hexToHslTriplet(readableOn(primary));
    const main = hexToHslTriplet(primary);
    Object.assign(vars, {
      "--primary": main,
      "--primary-foreground": onPrimary,
      "--accent": main,
      "--accent-foreground": onPrimary,
      "--ring": main,
      "--foreground": main,
      "--card-foreground": main,
      "--popover-foreground": main,
      "--secondary-foreground": main,
      "--sidebar-foreground": main,
      "--sidebar-primary": main,
      "--sidebar-primary-foreground": onPrimary,
      "--sidebar-accent-foreground": main,
      "--sidebar-ring": main,
    });
  }
  return vars;
}

// Every variable themeVariables can set, so switching company clears the previous one's overrides.
const ALL_THEME_VARS = Object.keys(
  themeVariables({ brand_primary: "#123456", brand_secondary: "#123456" })
);

export function applyTheme(t: Pick<TenantBranding, "brand_primary" | "brand_secondary"> | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  ALL_THEME_VARS.forEach((k) => root.style.removeProperty(k));
  // The default (gold on the platform purple) is what the stylesheet already says; only a company's own colours are set.
  if (t && (isHex(t.brand_primary) || isHex(t.brand_secondary))) {
    Object.entries(themeVariables(t)).forEach(([k, v]) => root.style.setProperty(k, v));
  }
}

/** The app's icon: shown on the platform login and used as the tab icon until a company or the platform sets its own. */
export const OPSLINK_ICON =
  "https://d64gsuwffb70l.cloudfront.net/68429d3677b256393c99965a_1765420434343_43832318.png";
const DEFAULT_ICON = OPSLINK_ICON;

export function applyDocumentBranding(opts: { title: string; faviconUrl?: string | null }): void {
  if (typeof document === "undefined") return;
  document.title = opts.title;
  const href = opts.faviconUrl || DEFAULT_ICON;
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

// ---------------------------------------------------------------------------
// Loading / saving
// ---------------------------------------------------------------------------

const TENANT_CACHE_KEY = "dms.tenantBranding";
const PLATFORM_CACHE_KEY = "dms.platformBranding";

const readCache = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const writeCache = (key: string, value: unknown) => {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch {
    /* private mode etc. */
  }
};

export const cachedTenantBranding = () => readCache<TenantBranding>(TENANT_CACHE_KEY);
export const cachedPlatformBranding = () => readCache<PlatformBranding>(PLATFORM_CACHE_KEY);
export const rememberTenantBranding = (b: TenantBranding | null) => writeCache(TENANT_CACHE_KEY, b);

export async function loadPlatformBranding(): Promise<PlatformBranding> {
  const { data, error } = await supabase.rpc("get_platform_branding");
  if (error || !data) return cachedPlatformBranding() ?? DEFAULT_PLATFORM;
  const merged = { ...DEFAULT_PLATFORM, ...(data as Partial<PlatformBranding>) };
  writeCache(PLATFORM_CACHE_KEY, merged);
  return merged;
}

export async function resolveTenantByCode(code: string): Promise<TenantBranding | null> {
  const { data, error } = await supabase.rpc("resolve_tenant_by_code", { p_code: code });
  if (error || !data) return null;
  return data as TenantBranding;
}

export async function loadMyTenantBranding(): Promise<TenantBranding | null> {
  const { data, error } = await supabase.rpc("get_my_tenant_branding");
  if (error || !data) return null;
  return data as TenantBranding;
}

export async function saveTenantBranding(
  tenantId: string,
  b: Pick<TenantBranding, "display_name" | "logo_url" | "favicon_url" | "brand_primary" | "brand_secondary" | "brand_background" | "login_tagline">
): Promise<void> {
  const { error } = await supabase.rpc("update_tenant_branding", {
    p_tenant_id: tenantId,
    p_display_name: b.display_name,
    p_logo_url: b.logo_url,
    p_favicon_url: b.favicon_url,
    p_brand_primary: b.brand_primary,
    p_brand_secondary: b.brand_secondary,
    p_brand_background: b.brand_background,
    p_login_tagline: b.login_tagline,
  });
  if (error) throw error;
}

export async function savePlatformBranding(b: PlatformBranding): Promise<void> {
  const { error } = await supabase.rpc("save_platform_branding", {
    p_product_name: b.product_name,
    p_product_subtitle: b.product_subtitle,
    p_tagline: b.tagline,
    p_logo_url: b.logo_url,
    p_favicon_url: b.favicon_url,
  });
  if (error) throw error;
}

/** Uploads a logo / icon to the public app-branding bucket and returns its public URL. */
export async function uploadBrandingAsset(file: File, folder: string): Promise<string> {
  const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
  const path = `${folder}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("app-branding").upload(path, file, { upsert: true, contentType: file.type || undefined });
  if (error) throw error;
  return supabase.storage.from("app-branding").getPublicUrl(path).data.publicUrl;
}

/** The title shown in the browser tab: the company name once chosen, otherwise the platform name. */
export const documentTitle = (platform: PlatformBranding, tenant: TenantBranding | null): string =>
  tenant ? `${tenant.display_name || tenant.tenant_name}` : platform.product_name;
